import React, { useState } from 'react';
import { useGetMany, useTranslate } from 'react-admin';
import { Box, Chip, Tooltip, styled } from '@material-ui/core';
import { Event, PlaceOutlined, EmojiEventsOutlined } from '@material-ui/icons'
import moment from 'moment-timezone';

import { getSurfaceCity, getSurfaceName } from '../../../surfaces/util';
import { TeamIcon } from '../../../icons';
import { useAssignmentsContext } from './AssignmentsContext';

const InfoChip = styled(Chip)({
  margin: 2,
})

const DateChip = ({ game }) => {
  const [ alternateFormat, setAlternateFormat ] = useState(false);
  const timeFormat = alternateFormat ? 'h:mmA' : 'HH:mm';
  const start = moment.tz(game.startTime, game.timezone).format(`dddd, LL, ${timeFormat}`)
  const end = moment.tz(game.endTime, game.timezone).format(`${timeFormat} z`)
  const date = `${start} - ${end}`

  const toggle = e => {
    e.stopPropagation();
    setAlternateFormat(show => !show);
  }
  return <InfoChip variant="outlined" label={date} icon={<Event fontSize="small" />} onClick={toggle} />
}

const SurfaceChip = ({ game }) => {
  const [ showAddress, setShowAddress ] = useState(false);
  const { data: surfaceData } = useGetMany('surfaces', [ game.arenaId ])
  const surface = surfaceData && surfaceData[0]
  if (!surface) return <InfoChip variant="outlined" label="..." icon={<PlaceOutlined fontSize="small" />} />

  const toggle = e => {
    e.stopPropagation();
    setShowAddress(show => !show);
  }
  const label = !showAddress ? getSurfaceName(surface) : getSurfaceCity(surface);
  return <InfoChip variant="outlined" label={label} icon={<PlaceOutlined fontSize="small" />} onClick={toggle} />
}

const ScheduleChip = ({ game }) => {
  const [ showName, setShowName ] = useState(true);
  const { data: scheduleData } = useGetMany('schedules', [ game?.scheduleId ], { enabled: game?.scheduleId != null })
  const schedule = scheduleData?.[0]
  const categoryId = schedule?.categoryId;
  const { data: categories } = useGetMany('categories', [ categoryId ], { enabled: categoryId != null })
  const category = categories?.[0]
  if (!schedule) return <InfoChip variant="outlined" label="..." icon={<EmojiEventsOutlined fontSize="small" />} />;

  const toggle = e => {
    e.stopPropagation();
    setShowName(show => !show);
  }
  const label = showName ? schedule.name : category?.name ? `${category.name} ${schedule.type}` : schedule.type;
  return <InfoChip variant="outlined" label={label} icon={<EmojiEventsOutlined fontSize="small" />} onClick={toggle} />
}

const TeamsChip = ({ game }) => {
  const translate = useTranslate();
  const teamIds = [game.homeTeamId, game.awayTeamId].filter(Boolean);
  const hasTeam = teamIds.length;
  const { data: teams, loading, error } = useGetMany('teams', teamIds, { enabled: hasTeam })

  if (error) return null;
  if (loading) return <InfoChip variant="outlined" label="..." icon={<TeamIcon fontSize="small" />} />;
  if (!game || !hasTeam || !teams || !teams.filter(Boolean).length) return null;

  const tbd = translate('resources.games.labels.tbd');
  const homeTeam = teams.find(team => team.id === game.homeTeamId);
  const awayTeam = teams.find(team => team.id === game.awayTeamId);
  return <InfoChip variant="outlined" label={`${awayTeam?.name || tbd} @ ${homeTeam?.name || tbd}`} icon={<TeamIcon fontSize="small" />} />
}

const ExpandChip = ({ onClick }) => {
  const translate = useTranslate();
  return <Tooltip title={translate('ra.action.tap_for_more')}>
    <InfoChip variant="outlined" label="..." onClick={onClick} />
  </Tooltip>
}

export default () => {
  const { game, settings } = useAssignmentsContext()
  const [ expanded, setExpand ] = useState(false);
  const expand = () => setExpand(true);
  if (!game || !settings?.enabled) return null;

  return <Box padding={1} onClick={expand}>
    <DateChip game={game} />
    <SurfaceChip game={game} />
    {!expanded && <ExpandChip onClick={expand} />}
    {expanded && <ScheduleChip game={game} />}
    {expanded && <TeamsChip game={game} />}
  </Box>
}
